import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Paper,
  IconButton,
  Tooltip,
  useTheme,
  CircularProgress,
} from '@mui/material';
import {
  NotificationsTwoTone,
  DoneAllTwoTone,
  ChevronRightTwoTone,
  AccessTimeTwoTone,
} from '@mui/icons-material';
import { notificationService, NotificationItem } from '@/services/notification/notificationService';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

export const NotificationPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchNotifications = async (pageNum: number, reset = false) => {
    try {
      setLoading(true);
      const response = await notificationService.getNotifications(pageNum);
      if (response.success) {
        if (reset) {
          setNotifications(response.data.notifications);
        } else {
          setNotifications(prev => [...prev, ...response.data.notifications]);
        }
        setHasMore(pageNum < response.data.pagination.pages);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications(1, true);
  }, []);

  const handleNotificationClick = async (notification: NotificationItem) => {
    if (!notification.isRead) {
      try {
        await notificationService.markAsRead(notification._id);
        // Optimistically update UI
        setNotifications(prev =>
          prev.map(n => (n._id === notification._id ? { ...n, isRead: true } : n))
        );
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }
    // Navigate to feed
    navigate(`/talky?feedId=${notification.feedId}`);
  };

  const handleMarkAllAsRead = async () => {
    // Current backend doesn't have "mark all" but we could loop or add it
    // For now, let's just mark the ones visible as read
    const unreadIds = notifications.filter(n => !n.isRead).map(n => n._id);
    for (const id of unreadIds) {
      try {
        await notificationService.markAsRead(id);
      } catch (e) {}
    }
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
  };

  const formatShortTime = (date: string) => {
    const m = moment(date);
    const now = moment();
    const diffInSeconds = now.diff(m, 'seconds');
    
    if (diffInSeconds < 60) return 'now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d`;
    if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)}mo`;
    return `${Math.floor(diffInSeconds / 31536000)}y`;
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 1, sm: 3 } }}>
      <Paper elevation={0} sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44 }}>
            <NotificationsTwoTone />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Stay updated with your network
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Mark all as read">
          <IconButton onClick={handleMarkAllAsRead} color="primary">
            <DoneAllTwoTone />
          </IconButton>
        </Tooltip>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <List sx={{ p: 0 }}>
          {notifications.map((notification, index) => (
            <React.Fragment key={notification._id}>
              <ListItem
                component="div"
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  cursor: 'pointer',
                  py: 2,
                  px: 3,
                  bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                  '&:hover': {
                    bgcolor: notification.isRead ? 'action.hover' : 'action.selected',
                  },
                  borderLeft: notification.isRead ? '4px solid transparent' : `4px solid ${theme.palette.primary.main}`,
                  transition: 'all 0.2s',
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    src={notification.sender.profilePictureId?.url}
                    sx={{ width: 48, height: 48 }}
                  >
                    {notification.sender.name.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="subtitle1" fontWeight={notification.isRead ? 500 : 700}>
                        {notification.sender.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                        <AccessTimeTwoTone sx={{ fontSize: 14 }} />
                        <Typography variant="caption">
                          {formatShortTime(notification.createdAt)}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          fontWeight: notification.isRead ? 400 : 500,
                          lineHeight: 1.4,
                        }}
                      >
                        {notification.type === 'feed_creation' ? 'shared a new post: ' : 'Super Admin posted: '}
                        {notification.description}
                      </Typography>
                    </Box>
                  }
                />
                <ChevronRightTwoTone sx={{ color: 'action.active', ml: 1, opacity: 0.5 }} />
              </ListItem>
              {index < notifications.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!loading && notifications.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
              <Avatar sx={{ bgcolor: 'action.hover', width: 80, height: 80, mx: 'auto', mb: 2 }}>
                <NotificationsTwoTone sx={{ fontSize: 40, color: 'text.disabled' }} />
              </Avatar>
              <Typography variant="h6" color="text.secondary">
                No notifications yet
              </Typography>
              <Typography variant="body2" color="text.disabled">
                When someone you follow posts, you'll see it here.
              </Typography>
            </Box>
          )}

          {!loading && hasMore && (
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <IconButton onClick={() => { setPage(p => p + 1); fetchNotifications(page + 1); }} color="primary">
                <Typography variant="button">Load More</Typography>
              </IconButton>
            </Box>
          )}
        </List>
      </Paper>
    </Box>
  );
};
